import type { Logger } from "@expert/logging";
import type {
    GaiaErrorBody,
    GaiaErrorMessage,
    ProactiveMessagePayload,
    ReactiveMessagePayload,
} from "@expert/shared-types";

import { handleIncomingMessage, sendFailureMetric, useTimelineStore } from "@asurion-core/expert-workspace-timeline";
import { type TimelineFeatures, useFeatures } from "@expert/features";
import {
    type GaiaWebSocketResponse,
    gaiaWsEventBus,
    isProactiveErrorMessage,
    isProactiveGuidanceMessage,
    isProactiveMessage,
    isProactiveRecommendationMessage,
    isReactiveErrorMessage,
    isReactiveMessage,
} from "@expert/gaia";
import { triggerProactiveSound, triggerTabNotification } from "@expert/proactive-notifications";
import { clearDeviceProbe, isDeviceProbe, setDeviceInfo } from "@expert/solve-tools";
import { useEffect, useMemo } from "react";

import { isDeadAirCorrelationId } from "@/dead-air/deadAirCorrelationRegistry";

import { handleGuidedFlowRecommendation } from "../../utils/";

export const useGaiaMessageEvents = (loggerProp: Logger) => {
    const {
        features: { isGuidedFlowRecommendationEnabled, isProactiveEnabled, isProactiveGuidanceEnabled },
    } = useFeatures<TimelineFeatures>();
    const logger = useMemo(() => loggerProp.child({ module: "useGaiaMessageEvents" }), [loggerProp]);

    useEffect(() => {
        // Reactive Message Handling
        const gaiaEventBusMessageSuccess = gaiaWsEventBus.on(
            "gaia_ws_orchestrator-invoke-success",
            ({ body, correlationId }: GaiaWebSocketResponse) => {
                /*
                    The orchestrator invoke success event returns reactive,
                    proactive, and autocomplete messages. Both proactive and
                    reactive messages are being handled in this event handler.
                    Auto complete messages are being handled in the useGaiaAutoCompleteEvents
                    handler.
                */
                // Dead-air classification asks reuse this path but are consumed out-of-band by
                // requestDeadAirClassification; skip them so their reply never renders as a bubble.
                if (isDeadAirCorrelationId(correlationId)) return;
                if (!isReactiveMessage(body as ReactiveMessagePayload)) return;

                const reactiveBodyForProbe = body as ReactiveMessagePayload;
                if (isDeviceProbe(correlationId)) {
                    clearDeviceProbe(correlationId);
                    setDeviceInfo(
                        {
                            sessionId: reactiveBodyForProbe.sessionGroupId,
                            externalDevices: reactiveBodyForProbe.externalDevices,
                            detectedDevices: reactiveBodyForProbe.devices,
                            origin: "probe",
                        },
                        logger,
                    );
                    return;
                }

                handleIncomingMessage(body as ReactiveMessagePayload, logger);

                // Clear retryPayload in case the orchestrator timeout fired before this response arrived
                const reactiveBody = body as ReactiveMessagePayload;
                useTimelineStore.getState().updateMessage(reactiveBody.sessionGroupId, reactiveBody.messageId, {
                    metadata: { retryPayload: undefined },
                });
            },
        );

        // Proactive Message Handling
        const gaiaEventBusProactiveMessageReceived = gaiaWsEventBus.on(
            "gaia_ws_message-suggestion",
            ({ body }: GaiaWebSocketResponse) => {
                const gaiaSuggestion = body as unknown as ProactiveMessagePayload;

                if (isProactiveGuidanceEnabled && isProactiveGuidanceMessage(gaiaSuggestion)) {
                    if (gaiaSuggestion.suggestion.metadata?.suppressionFlag) {
                        logger.info(
                            {
                                messageId: gaiaSuggestion.suggestion.messageId,
                                suppressionReason: gaiaSuggestion.suggestion.metadata.suppressionReason,
                            },
                            "GAIA | proactive-guidance message suppressed",
                        );
                        return;
                    }

                    handleIncomingMessage(gaiaSuggestion, logger);
                    triggerTabNotification("Expert Workspace", isProactiveGuidanceEnabled);
                    triggerProactiveSound(isProactiveGuidanceEnabled);
                    return;
                }

                if (isGuidedFlowRecommendationEnabled && isProactiveRecommendationMessage(gaiaSuggestion)) {
                    handleGuidedFlowRecommendation(gaiaSuggestion, logger);
                    return;
                }

                // All messages are coming through this event (reactive messages + proactive messages)
                if (!isProactiveEnabled || !isProactiveMessage(gaiaSuggestion)) return;

                // TODO: error messages should not return with messageType = proactive
                // working with gaia team to resolve this
                if (isProactiveErrorMessage(gaiaSuggestion)) {
                    logger.warn(
                        {
                            originalError: gaiaSuggestion.suggestion.message,
                        },
                        "GAIA | websocket proactive error message response",
                    );
                    return;
                }

                handleIncomingMessage(gaiaSuggestion, logger);
                triggerTabNotification("Expert Workspace", isProactiveEnabled);
                triggerProactiveSound(isProactiveEnabled);
            },
        );

        const gaiaEventBusOrchestratorFailure = gaiaWsEventBus.on(
            "gaia_ws_orchestrator-invoke-error",
            ({ body, correlationId }: GaiaWebSocketResponse) => {
                // Dead-air asks handle their own errors out-of-band (by correlationId); ignore here.
                if (isDeadAirCorrelationId(correlationId)) return;
                const errorMessage = body as GaiaErrorMessage;

                let errorBody: GaiaErrorBody;
                try {
                    errorBody = JSON.parse(errorMessage.body) as GaiaErrorBody;
                } catch {
                    logger.error({ errorMessage }, "GAIA | Failed to parse error body");
                    return;
                }

                if (isReactiveErrorMessage(errorBody)) {
                    sendFailureMetric("botFailure");
                    useTimelineStore.getState().updateMessage(errorBody.sessionGroupId, errorBody.messageId, {
                        metadata: {
                            retryPayload: {
                                text: errorBody.message,
                                type: "Expert",
                            },
                        },
                    });
                }

                logger.warn(
                    {
                        errorMessage,
                        errorBody,
                    },
                    "GAIA | websocket error message response",
                );
            },
        );

        const gaiaEventBusSessionContextFailure = gaiaWsEventBus.on(
            "gaia_ws_update-session-context-error",
            ({ body }: GaiaWebSocketResponse) => {
                const { ...errorMessage } = body as GaiaErrorMessage;
                const { ...errorBody } = JSON.parse(errorMessage.body) as unknown as GaiaErrorBody;

                logger.warn(
                    {
                        errorMessage,
                        errorBody,
                    },
                    "GAIA | update session context error message response",
                );
            },
        );

        return () => {
            gaiaEventBusMessageSuccess();
            gaiaEventBusOrchestratorFailure();
            gaiaEventBusProactiveMessageReceived();
            gaiaEventBusSessionContextFailure();
        };
    }, [isGuidedFlowRecommendationEnabled, isProactiveEnabled, isProactiveGuidanceEnabled, logger]);
};
